import { useState } from 'react';
import { useLazyQuery, useMutation } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { doAndRethrow } from 'growth-payments-core/promise/doAnd';
import { navigateTo } from 'growth-payments-core/urls/utils';
import { getCommerceHomeUrl } from 'growth-payments-core/urls/urls';
import { TrackedLink } from 'growth-payments-core/usage-tracker/components/Link';
import { events } from 'growth-payments-core/usage-tracker/events';
import EmptyStateEvents from 'growth-payments-core/constants/emptyStateEvents';
import { GET_PAYMENTS_ELIGIBILITY } from '../queries/paymentAccountQueries';
import { GET_OAUTH_SESSION_LATEST_STATUS } from '../queries/stripeOAuthSessionQueries';
import { SET_TOS_ACCEPTANCE } from '../queries/tosQueries';
import { OAUTH_ACCOUNT_ALREADY_CONNECTED, OAUTH_UNSUPPORTED_COUNTRY } from '../constants/networkErrors';
import { jsx as _jsx } from "react/jsx-runtime";
/*
Poll the latest status endpoint to determine if an OAuth account has been connected.

BE receives the OAuthorPortalGrantEvent, then creates a connected account.
FE has to poll for account creation status and once it has been "SUCCESSFULLY_CONNECTED", we stop polling.
FE also submits ToS acceptance, because that requires a connected account to exist first.

https://hubspot.slack.com/archives/C05TL4HQA3H/p1698159553245919?thread_ts=1698155505.849419&cid=C05TL4HQA3H
*/

const STRIPE_INTERNATIONAL_AVAILABILITY = 'https://knowledge.hubspot.com/payments/connect-your-stripe-account-as-a-payment-processor-in-hubspot#international-availability';
const useOAuthSessionLatestStatus = ({
  maxNumPolling = 15,
  onErrorHandler,
  onSuccessHandler,
  onFriendlyError
}) => {
  const [pollCount, setPollCount] = useState(1);
  const [setTosAcceptance] = useMutation(SET_TOS_ACCEPTANCE);
  const [refetchEligibility, {
    data: eligibilityData
  }] = useLazyQuery(GET_PAYMENTS_ELIGIBILITY, {
    fetchPolicy: 'network-only',
    onError: () => navigateTo(getCommerceHomeUrl())
  });
  const [getOAuthSessionLatestStatus, {
    loading,
    stopPolling
  }] = useLazyQuery(GET_OAUTH_SESSION_LATEST_STATUS, {
    pollInterval: 1000,
    notifyOnNetworkStatusChange: true,
    // needed so onCompleted is called on each interval
    onCompleted({
      oAuthSessionLatestStatus
    }) {
      if (oAuthSessionLatestStatus === 'SUCCESSFULLY_CONNECTED') {
        stopPolling === null || stopPolling === void 0 || stopPolling();
        refetchEligibility(); //prime the cache for eligibility to be read by the modal after contents are loaded
        setTosAcceptance().catch(doAndRethrow());
        if (onSuccessHandler) {
          onSuccessHandler();
        }
      } else if (pollCount === maxNumPolling) {
        stopPolling === null || stopPolling === void 0 || stopPolling();
        onErrorHandler === null || onErrorHandler === void 0 || onErrorHandler(new Error('connected-account-polling-failed'));
      } else {
        setPollCount(pollCount + 1);
      }
    },
    onError(error) {
      stopPolling === null || stopPolling === void 0 || stopPolling();
      const networkErrorSubcategory = error.networkError && error.networkError.responseJSON && error.networkError.responseJSON.subCategory;
      if (networkErrorSubcategory === OAUTH_ACCOUNT_ALREADY_CONNECTED) {
        FloatingAlertStore.addAlert({
          id: 'oauth-account-already-connected',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'OauthAccountOnboardingModal.errorAlert.alreadyConnectedAccount.title'
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'OauthAccountOnboardingModal.errorAlert.alreadyConnectedAccount.message'
          }),
          type: 'danger'
        });
        if (onFriendlyError) onFriendlyError();
      } else if (networkErrorSubcategory === OAUTH_UNSUPPORTED_COUNTRY) {
        FloatingAlertStore.addAlert({
          id: 'oauth-unsupported-country',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'OauthAccountOnboardingModal.errorAlert.unsupportedCountry.title'
          }),
          message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "OauthAccountOnboardingModal.errorAlert.unsupportedCountry.message_jsx",
            options: {
              href: STRIPE_INTERNATIONAL_AVAILABILITY,
              event: events.paymentSettingsInteraction,
              external: true,
              trackingProperties: {
                action: EmptyStateEvents.LINK_CLICK,
                location: 'payment-waitlist-location-not-supported-byos'
              }
            },
            elements: {
              Link: TrackedLink
            }
          }),
          type: 'danger'
        });
        if (onFriendlyError) onFriendlyError();
      } else {
        throw error;
      }
      if (onErrorHandler) {
        onErrorHandler();
      }
    }
  });
  return {
    loading: loading || !eligibilityData,
    // ensure loading is true until we have eligibilityData
    getOAuthSessionLatestStatus
  };
};
export default useOAuthSessionLatestStatus;